import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Loader2, Plus, X } from 'lucide-react'
import {
  ORG_TYPES,
  createOrganization,
  deleteOrganization,
  fetchOrganizations,
  updateOrganization,
} from '@/lib/api/spaces.js'
import RowMenu from '@/components/common/RowMenu.jsx'
import OrgMark from '@/components/common/OrgMark.jsx'

export default function OrganizationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrganizations,
  })

  const [dialog, setDialog] = useState(null)

  const createMutation = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      setDialog(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }) => updateOrganization(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      setDialog(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orgs'] }),
  })

  const handleDelete = (org) => {
    const siteCount = (org.sites || []).length
    const floorCount = countFloors(org)
    const msg =
      siteCount + floorCount > 0
        ? `Delete "${org.name}"? This also removes all sites, buildings, and floors inside it.`
        : `Delete "${org.name}"?`
    if (window.confirm(msg)) deleteMutation.mutate(org.id)
  }

  const deleteError = deleteMutation.error?.uiMessage

  return (
    <main className="app-content">
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Organizations</h1>
          <div className="page-header__sub">
            Manage the top-level customers and tenants that own sites, buildings, and floors.
          </div>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setDialog({ mode: 'create' })}
        >
          <Plus size={14} /> New organization
        </button>
      </header>

      {deleteError && (
        <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>
          <span>{deleteError}</span>
          <button type="button" className="btn btn--sm" onClick={() => deleteMutation.reset()}>
            Dismiss
          </button>
        </div>
      )}

      <section className="card card--lg card--flush">
        {isLoading ? (
          <div className="table__empty">Loading organizations...</div>
        ) : orgs.length === 0 ? (
          <div className="table__empty">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              No organizations yet
            </div>
            <div style={{ marginBottom: 14 }}>Create one to start mapping spaces.</div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setDialog({ mode: 'create' })}
            >
              <Plus size={14} /> New organization
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Type</th>
                <th>Status</th>
                <th>Sites</th>
                <th>Floors</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => {
                const open = () => navigate(`/organizations/${org.id}`)
                const onKeyDown = (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    open()
                  }
                }
                return (
                  <tr
                    key={org.id}
                    className="is-interactive"
                    role="link"
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={onKeyDown}
                  >
                    <td>
                      <div className="row">
                        <OrgMark />
                        <div>
                          <div style={{ fontWeight: 600 }}>{org.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{org.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td>{typeLabel(org.type)}</td>
                    <td><span className="badge">{org.status || 'active'}</span></td>
                    <td>{(org.sites || []).length}</td>
                    <td>{countFloors(org)}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                        <ChevronRight size={16} color="var(--text-muted)" />
                        <RowMenu
                          label="Organization actions"
                          onEdit={() => setDialog({ mode: 'edit', org })}
                          onDelete={() => handleDelete(org)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      {dialog && (
        <OrgDialog
          mode={dialog.mode}
          org={dialog.org}
          submitting={createMutation.isPending || updateMutation.isPending}
          error={
            createMutation.error?.uiMessage ||
            updateMutation.error?.uiMessage ||
            null
          }
          onClose={() => {
            createMutation.reset()
            updateMutation.reset()
            setDialog(null)
          }}
          onSubmit={(values) => {
            if (dialog.mode === 'create') createMutation.mutate(values)
            else updateMutation.mutate({ id: dialog.org.id, patch: values })
          }}
        />
      )}
    </main>
  )
}

function OrgDialog({ mode, org, submitting, error, onClose, onSubmit }) {
  const [name, setName] = useState(org?.name || '')
  const [slug, setSlug] = useState(org?.slug || '')
  const [type, setType] = useState(org?.type || 'other')
  const [slugTouched, setSlugTouched] = useState(Boolean(org))

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleNameChange = (v) => {
    setName(v)
    if (!slugTouched) {
      setSlug(
        v.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-'),
      )
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim() || submitting) return
    onSubmit({ name: name.trim(), slug: slug || undefined, type })
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="org-dialog-title" className="modal__title">
            {mode === 'create' ? 'New organization' : 'Edit organization'}
          </h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span className="auth-field__label">Name</span>
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="VA Northern California Health Care System"
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Slug</span>
            <input
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugTouched(true) }}
              placeholder="va-northern-california"
              pattern="[a-z0-9-]*"
            />
            <span className="muted" style={{ fontSize: 11 }}>
              Lowercase letters, numbers, dashes. Auto-derived from name unless you edit it.
            </span>
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {ORG_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !name.trim()}>
              {submitting ? (
                <><Loader2 size={14} className="spin" /> Saving...</>
              ) : mode === 'create' ? 'Create organization' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function typeLabel(value) {
  return ORG_TYPES.find((type) => type.value === value)?.label || value || 'Other'
}

function countFloors(org) {
  return (org.sites || []).reduce(
    (sum, site) =>
      sum + (site.buildings || []).reduce(
        (buildingSum, building) => buildingSum + (building.floors || []).length,
        0,
      ),
    0,
  )
}
